import "./datamodel-field-modal.scss";
import { useContext, useEffect, useState } from "react";

import { datamodelEnumerationListUrl } from "../../../../../platform/constants/backend-constants";
import CheckboxInput from "../../../../../shared/components/checkbox-input/checkbox-input";
import SimpleModal from "../../../../../shared/components/simple-modal/simple-modal";
import AuthContext from "../../../../../shared/context/auth-context";
import { IEnumerationOption } from "../../../../../shared/definitions/datatypes";
import useHttp from "../../../../../shared/hooks/use-http";
import { useHttpGet } from "../../../../../shared/hooks/use-httpNonTable";
import { getAllData, getData } from "../../../../../shared/utils/api";
import { IObject, validateLoadingStatusNew } from "../../../../../shared/utils/utils";
import { noop } from "../../../../utils";
import SearchInput from "../../../search-input/search-input";
import { IWidgetModalProps } from "../../widget-utils";
import { IDateWidget } from "../../widgets/date-widget/date-widget";
import { IListWidget } from "../../widgets/list-widget/list-widget";
import { INumberWidget } from "../../widgets/number-widget/number-widget";
import { IFieldWidget } from "../../widgets/text-field-widget/text-field-widget";
import Spinner from "@/shared/components/spinner/spinner";

export interface IDatamodelFieldModal {
  type: "datamodelField";
  datamodelId?: string;
  datamodelType: "event" | "entity" | "alertcase";
}

interface IDatamodelFieldModalProps extends IWidgetModalProps<IFieldWidget | INumberWidget | IDateWidget | IListWidget> {
  selectedDatamodelId?: string;
  datamodelType: "event" | "entity" | "alertcase";
}

const DatamodelFieldModal = ({
  selectedDatamodelId,
  modalOpen,
  widgetData,
  datamodelType,
  onWidgetChanged
}: IDatamodelFieldModalProps) => {
  const emptyFieldWidget: IFieldWidget | INumberWidget | IDateWidget | IListWidget = {
    id: "",
    label: "",
    modalType: "datamodelField",
    type: "field",
    required: false,
    readonly: false,
    primaryKey: false,
    datamodel_type: "",
    datamodel_id: "",
    field_id: "",
    modalData: {
      datamodelType: datamodelType
    }
  };
  
  const authCtx = useContext(AuthContext);

  const [modalData, setModalData] = useState<any>(widgetData?.id ? widgetData : {...emptyFieldWidget}); // eslint-disable-line
  const [showModal, setShowModal] = useState(modalOpen || false);
  const [datamodelFields, setDatamodelFields] = useState<IEnumerationOption[]>([]);
  const [lists, setLists] = useState<IEnumerationOption[]>([]);
  
  const { sendRequest: getDatamodelDetails, data: getDatamodelData, status: getDatamodelStatus, error: getDatamodelError } = useHttpGet(getData, false, authCtx.token, undefined, "platform");
  const { sendRequest: getEventConsolidation, status: getEventConsolidationStatus, data: getEventConsolidationData, error: getEventConsolidationError } = useHttp(getAllData, false, authCtx.token, "eventConsolidationInfo", "manager");
  const { sendRequest: getLists, data: getListsData, status: getListsStatus, error: getListsError } = useHttp(getAllData, false, authCtx.token, undefined, "platform");

  useEffect(() => {
    if (datamodelType === "event") {
      getEventConsolidation(`event_consolidation/${selectedDatamodelId}`);
    }
  }, [selectedDatamodelId]);

  useEffect(() => {
    getLists(datamodelEnumerationListUrl);
  }, []);

  useEffect(() => {
    if (getListsStatus === "completed" && !getListsError && getListsData) {
      setLists(getListsData.rows);
    }
  }, [getListsData, getListsStatus, getListsError]);

  useEffect(() => {
    if (getEventConsolidationStatus === "completed" && !getEventConsolidationError && getEventConsolidationData && getDatamodelData && getDatamodelStatus === "completed" && !getDatamodelError) {
      const filteredDatamodelFields = getDatamodelData.fields.filter((field : IObject) => 
        field.id !== getEventConsolidationData.primary_datetime_column_id &&
        field.id !== getEventConsolidationData.process_type_column_id &&
        !field.primary_key);
      setDatamodelFields(filteredDatamodelFields.map((field : IObject) => ({
        id: field.input_field_name,
        name: field.field_name
      })));
    }
  }, [getEventConsolidationStatus, getEventConsolidationData, getEventConsolidationError, getDatamodelData, getDatamodelStatus, getDatamodelError]);

  useEffect(() => {
    setShowModal(modalOpen || false);
    setModalData(widgetData?.id ? widgetData : {...emptyFieldWidget});
  }, [modalOpen, widgetData]);

  useEffect(() => {
    if (selectedDatamodelId) {
      getDatamodelDetails(`datamodel_handle_referencing_field/${datamodelType}/${selectedDatamodelId}`);
    }
  }, [selectedDatamodelId]);

  useEffect(() => {
    if (getDatamodelData && !getDatamodelError && getDatamodelStatus === "completed" && !getEventConsolidationStatus) {
      setDatamodelFields(getDatamodelData.fields.map((field : IObject) => ({
        id: field.input_field_name,
        name: field.field_name
      })));
    }
  }, [getDatamodelData, getDatamodelStatus, getDatamodelError]);

  const handleOnDatamodelChange = (selectedOption: IEnumerationOption | undefined) => {
    if (!selectedOption) {
      return;
    }

    const datamodelField = getDatamodelData?.fields?.find((field : IObject) => field.input_field_name === selectedOption?.id);
    if (!datamodelField) {
      return;
    }
    
    modalData.primaryKey = datamodelField.primary_key;
    modalData.datamodel_type = datamodelType;
    modalData.datamodel_id = selectedDatamodelId;
    modalData.field_id = datamodelField.id;

    if (datamodelField.field_datatype === "text") {
      modalData.type = "field";
    } else if (datamodelField.field_datatype === "integer") {
      modalData.type = "integer";
    } else if (datamodelField.field_datatype === "float") {
      modalData.type = "float";
    } else if (datamodelField.field_datatype === "boolean") {
      modalData.type = "boolean";
    } else if (datamodelField.field_datatype === "datetime") {
      modalData.type = "date";
    }

    modalData.id = selectedOption?.id || "";
    modalData.modalData["datamodelFieldId"] = selectedOption?.id || "";
    modalData.label = selectedOption?.name || "";

    setModalData({...modalData});
  };

  const renderModalBody = () => {
    return <div>
      <SearchInput
        label={"Field"}
        handleOnChange={handleOnDatamodelChange}
        data={datamodelFields}
        inputKey="datamodelFieldId"
        required={true}
        selectedOption={modalData?.modalData?.datamodelFieldId || ""}
      />
      <CheckboxInput
        label="Required"
        checked={modalData.required}
        onChange={() => setModalData({
          ...modalData,
          required: !modalData.required
        })}
      />
      <CheckboxInput
        label="Readonly"
        checked={modalData.readonly}
        onChange={() => setModalData({
          ...modalData,
          readonly: !modalData.readonly
        })}
      />
      {modalData.id && (modalData.type === "field" || modalData.type === "list") && 
        <div className="datamodelFieldModal">
          <CheckboxInput
            label="Text area"
            checked={modalData.textarea}
            onChange={() => setModalData({
              ...modalData,
              textarea: !modalData.textarea,
              listId: undefined // List and textarea are exclusive
            })}
          />
          <SearchInput
            label="List"
            handleOnChange={(selectedOption: IEnumerationOption | undefined) => setModalData({
              ...modalData,
              type: selectedOption?.id ? "list" : "field",
              textarea: false,
              listId: selectedOption?.id || "" // List and textarea are exclusive
            })}
            data={lists}
            inputKey="datamodelFieldId"
            required={false}
            selectedOption={modalData?.listId || ""}
          />
        </div>
      }
    </div>;
  };

  const handleModalSubmit = () => {
    onWidgetChanged(modalData as IFieldWidget | INumberWidget | IDateWidget | IListWidget);
    setShowModal(false);
    setModalData({...emptyFieldWidget});
  };

  const handleModalClosed = () => {
    onWidgetChanged(undefined);
    setShowModal(false);
    setModalData({...emptyFieldWidget});
  };

  const openModelFunction = () => {
    return () => {
      setShowModal(true);
    };
  }

  return <div className='datamodelFieldModal'>
    {validateLoadingStatusNew([getDatamodelStatus]) && <Spinner />}
    <span className="datamodelFieldModal-layoutButton" onClick={openModelFunction()}>
      {datamodelType === "alertcase" ? "Alert field" : "Field"} +
    </span>

    <SimpleModal 
      mode={"Field"} 
      onModalClickHandler={noop} 
      modalBody={renderModalBody()} 
      buttonText={"Add"} 
      handleModalClosed={handleModalClosed} 
      handleSubmit={handleModalSubmit} 
      show={showModal}
      allowScrollOnBody={false}
    />
  </div>;
};

export default DatamodelFieldModal;